import { useTranslation } from "@redotech/redo-i18n";
import BeakerIcon from "@redotech/redo-icons/beaker-02.svg";
import { AutomationOutreachSplitWithTreatments } from "@redotech/redo-merchant-marketing-rpc/schema/automation-outreach-splits/get-automation-outreach-split";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoEmptyState } from "@redotech/redo-web/arbiter-components/empty-state/redo-empty-state";
import { Flex } from "@redotech/redo-web/flex";
import { InspectorSection } from "@redotech/redo-web/inspector-v2";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";

export interface AutomationAbTestInspectorProps {
  existingTest: AutomationOutreachSplitWithTreatments | null;
  hasTemplate: boolean;
  onOpenEditor: () => void;
}

export const AutomationAbTestInspector = memo(
  function AutomationAbTestInspector({
    existingTest,
    hasTemplate,
    onOpenEditor,
  }: AutomationAbTestInspectorProps) {
    const { t } = useTranslation();
    if (existingTest) {
      const isActive = existingTest.split.active;
      const treatmentCount = existingTest.treatments.length;

      return (
        <InspectorSection label={t("A/B tests")}>
          <Flex dir="column" gap="md" w="full">
            <Flex align="center" justify="space-between" w="full">
              <Flex dir="column" gap="xs">
                <Text fontSize="xs" fontWeight="medium">
                  {existingTest.split.name}
                </Text>
                <Text fontSize="xxs" textColor="secondary">
                  {treatmentCount} variants
                  {isActive ? " (Active)" : " (Inactive)"}
                </Text>
              </Flex>
              <RedoButton
                hierarchy="secondary"
                onClick={onOpenEditor}
                size="xs"
                text={t("Edit")}
              />
            </Flex>
          </Flex>
        </InspectorSection>
      );
    }

    return (
      <InspectorSection label={t("A/B tests")}>
        {!hasTemplate ? (
          <RedoEmptyState
            Icon={BeakerIcon}
            subtext={t(
              "Add a template to this step before creating an A/B test",
            )}
            text={t("Add content first")}
            useNonArbiterPadding
          />
        ) : (
          <RedoEmptyState
            Icon={BeakerIcon}
            secondaryButtonProps={{
              onClick: onOpenEditor,
              text: t("Create A/B test"),
            }}
            subtext={t(
              "Run A/B tests to discover what resonates with your audience",
            )}
            text={t("No tests yet")}
            useNonArbiterPadding
          />
        )}
      </InspectorSection>
    );
  },
);
